import React, { useEffect, useRef, useState } from "react";
import { Attachment, DownloadRectangle } from "react-huge-icons/outline";
import { useTranslation } from "react-i18next";
import useSupportStore from "../../stores/chatStore";
import SupportService, { MessageResult } from "../../api/supportService";
import { useParams } from "react-router-dom";
import { useModalBusyLoadingStore } from "../../stores/modalBusyLoadingStore";
import useBreadcrumbsStore from "../../stores/breadcrumbStore";
import { ConstantRoutes } from "../../core/constantRoutes";
import TextWithBreaks from "../common/textWithBreaks/textWithBreaks";
import { File } from "react-huge-icons/solid";
import MessageBubble from "../common/chat/messageBubble";
import MessageContainer from "../common/chat/messageContainer";
import MessageTime from "../common/chat/messageTime";
import ChatPlaceholder from "../skeleton/chatPlaceholder";
import LoadingIndicator from "../common/loadingIndicator";

const SupportChat: React.FC = () => {
  const { t } = useTranslation();
  const { ticket } = useParams<{ ticket: string }>();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { setModalOpen } = useModalBusyLoadingStore();
  const [isLoaded, setIsLoaded] = useState(false);

  const { messages, fetchMessages, clearMessages, message, setMessage } =
    useSupportStore();
  const { addBreadcrumb, clearBreadcrumbs } = useBreadcrumbsStore();

  const supportService = new SupportService();

  useEffect(() => {
    clearBreadcrumbs();
    clearMessages();
    addBreadcrumb({ label: "Support", path: ConstantRoutes.support });
    addBreadcrumb({
      label: ticket ?? "?",
      path: ConstantRoutes.supportTicket + ticket,
    });

    setMessage("");
    fetchMessages(Number(ticket)).then(() => setIsLoaded(true));
  }, []);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSendMessage = async () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage.length > 0) {
      setIsLoaded(false);
      setMessage("");
      await supportService.sendMessage(Number(ticket), trimmedMessage);

      fetchMessages(Number(ticket)).then(() => setIsLoaded(true));
    }
  };

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsLoaded(false);
      setModalOpen(true);
      await supportService.uploadFile(Number(ticket), file);
      fetchMessages(Number(ticket)).then(() => setIsLoaded(true));
      setModalOpen(false);
    }
  };

  const downloadMessageFile = async (id: number) => {
    supportService.downloadMessageFile(Number(ticket), id);
  };

  return (
    <>
      <div className="bg-content2 md:w-7/12 lg:w-7/12 xl:w-2/3 max-md:w-full rounded-xl p-5 h-screen">
        <div
          className="bg-content3 rounded-xl p-5 overflow-y-auto h-4/6"
          ref={messagesEndRef}
        >
          {!isLoaded && <ChatPlaceholder />}
          {isLoaded && messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full">
              <div className="bg-content2 rounded-lg p-6 w-2/3 text-center">
                <p className="text-lg text-textprimary font-medium">
                  {t("Chat is empty")}
                </p>
              </div>
            </div>
          )}
          {isLoaded &&
            messages.length > 0 &&
            messages.map((message: MessageResult) => {
              const isMine = message.userId === "True";
              const isFile = message.chatMessageType === 1;
              const fileName = isFile ? message.text.split(";")[1] || "" : "";

              return (
                <MessageContainer key={message.id} isMine={isMine}>
                  <MessageTime time={new Date(message.createdOn)} />

                  <MessageBubble isMine={isMine} isFile={isFile}>
                    {isFile ? (
                      <div
                        className={`bg-content${
                          isMine ? "1" : "2"
                        }light rounded-md flex items-center gap-2`}
                      >
                        <File className="w-6 h-6" />
                        <div>{fileName}</div>
                        <button
                          className="text-center cursor-pointer rounded-lg h-10 w-10 flex items-center justify-center hover:animate-pulse transition duration-300 ease-in-out"
                          onClick={() => downloadMessageFile(message.id)}
                        >
                          <DownloadRectangle className="w-6 h-6" />
                        </button>
                      </div>
                    ) : (
                      <TextWithBreaks text={message.text} />
                    )}
                  </MessageBubble>
                </MessageContainer>
              );
            })}
        </div>

        <div className="flex bg-content3 rounded-xl p-2 mt-3 items-stretch">
          <textarea
            className="flex-grow resize-none outline-none px-2 py-1 bg-content3 max-h-60 overflow-y-auto"
            placeholder={t("Your message")}
            value={message}
            onChange={(event) => {
              setMessage(event.target.value);

              // автоувеличение высоты
              const textarea = event.target as HTMLTextAreaElement;
              textarea.style.height = "auto";
              textarea.style.height = `${textarea.scrollHeight}px`;
            }}
            onKeyDown={(event) => {
              if (event.key === "Enter" && event.shiftKey) {
                event.preventDefault();
                handleSendMessage();
              }
            }}
            rows={1}
          />
          <button
            className="bg-primary rounded-md w-28 ml-2 h-12"
            onClick={() => handleSendMessage()}
          >
            {t("Send")}
          </button>
          <button
            className="border border-divider rounded-md w-roundedcontact h-12 flex items-center
      justify-center hover:bg-primary transition duration-300 ease-in-out ml-2"
            onClick={handleFileSelect}
          >
            <Attachment className="w-6 h-6" />
          </button>
          <input
            type="file"
            className="hidden"
            ref={fileInputRef}
            onChange={handleFileUpload}
          />
        </div>
      </div>
    </>
  );
};

export default SupportChat;
